import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PrimaryButton, SecondaryButton } from "../../components/Button";

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    pendingCount: number;
    mode?: "save" | "discard";
    confirmText?: string;
    cancelText?: string;
    titleOverride?: string;
}

export default function ConfirmationModal({
                                              isOpen,
                                              onClose,
                                              onConfirm,
                                              pendingCount,
                                              mode = "save",
                                              confirmText,
                                              cancelText = "Cancel",
                                              titleOverride,
                                          }: ConfirmationModalProps) {
    const initialFocusRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await onConfirm();
            onClose();
        } catch (error) {
            console.error("Error during confirmation:", error);
            setIsLoading(false);
        }
    };

    const isDiscard = mode === "discard";
    const changesWord = pendingCount === 1 ? "change" : "changes";

    const title =
        titleOverride ??
        `Are you sure you want to ${isDiscard ? "discard" : "save"} ${pendingCount} ${changesWord}?`;

    const primaryLabel = confirmText ?? (isDiscard ? "Discard" : "Save");

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[1000]"
                initialFocus={initialFocusRef}
                onClose={isLoading ? () => {} : onClose}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="fixed inset-0 z-[1000]"
                        aria-hidden="true"
                        style={{ backgroundColor: "rgba(0,0,0,0.2)" }}
                    />
                </Transition.Child>

                <div className="fixed inset-0 z-[1001] overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative z-[1002] transform overflow-hidden rounded-lg bg-white px-6 py-8 text-center shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div ref={initialFocusRef} tabIndex={-1} aria-hidden="true" />

                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900"
                                >
                                    {title}
                                </Dialog.Title>

                                <div className="mt-4 flex justify-center gap-2">
                                    <SecondaryButton onClick={isLoading ? undefined : onClose}>
                                        {cancelText}
                                    </SecondaryButton>
                                    <PrimaryButton onClick={handleConfirm} isLoading={isLoading}>
                                        {primaryLabel}
                                    </PrimaryButton>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
